import React, { useState, useEffect } from 'react';
import borisApiService from '../services/BorisApiService';
import './SupportDashboard.css';

/**
 * SupportDashboard Component
 * Displays escalation tickets in a table format for support team
 */
const SupportDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    severity: 'all',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Fetch tickets from API
  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📊 Fetching tickets with filters:', filters);
      
      // Use the configured API service instead of direct fetch
      const data = await borisApiService.listEscalations(filters);
      console.log('✅ Tickets fetched successfully:', data);
      
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error fetching tickets:', err);
      setError(err.message);
      
      // Mock data for development/testing when API fails
      console.log('🔄 Using mock data for development');
      setTickets([
        {
          ticket_id: 'BSB-123456-ABC',
          contact_name: 'John Doe',
          contact_email: 'john@example.com',
          issue_type: 'Technical',
          severity: 'P2 - High',
          summary: 'Cannot access dashboard',
          status: 'open',
          created_at: '2024-01-15T10:30:00Z',
          session_id: 'session_123'
        },
        {
          ticket_id: 'BSB-123457-DEF',
          contact_name: 'Jane Smith',
          contact_email: 'jane@example.com',
          issue_type: 'Billing',
          severity: 'P3 - Normal',
          summary: 'Billing question about subscription',
          status: 'in_progress',
          created_at: '2024-01-15T09:15:00Z',
          session_id: 'session_124'
        },
        {
          ticket_id: 'BSB-123458-GHI',
          contact_name: 'Mike Johnson',
          contact_email: 'mike@example.com',
          issue_type: 'Account',
          severity: 'P1 - Critical',
          summary: 'Account locked out',
          status: 'resolved',
          created_at: '2024-01-15T08:45:00Z',
          session_id: 'session_125'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Update ticket status
  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      console.log(`🔄 Updating ticket ${ticketId} status to ${newStatus}`);
      
      // Use the configured API service instead of direct fetch
      await borisApiService.updateEscalationStatus(ticketId, newStatus);
      
      console.log('✅ Ticket status updated successfully');
      
      // Update local state
      setTickets(prevTickets =>
        prevTickets.map(ticket =>
          ticket.ticket_id === ticketId
            ? { ...ticket, status: newStatus }
            : ticket
        )
      );
    } catch (err) {
      console.error('❌ Error updating ticket status:', err);
      alert('Failed to update ticket status: ' + err.message);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'status-badge status-open';
      case 'in_progress':
        return 'status-badge status-progress';
      case 'resolved':
        return 'status-badge status-resolved';
      case 'closed':
        return 'status-badge status-closed';
      default:
        return 'status-badge status-unknown';
    }
  };

  // Get severity badge class
  const getSeverityBadgeClass = (severity) => {
    if (severity?.includes('P1')) return 'severity-badge severity-critical';
    if (severity?.includes('P2')) return 'severity-badge severity-high';
    if (severity?.includes('P3')) return 'severity-badge severity-normal';
    if (severity?.includes('P4')) return 'severity-badge severity-low';
    return 'severity-badge severity-unknown';
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Handle sort changes
  const handleSort = (sortBy) => {
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Fetch tickets on component mount and filter changes
  useEffect(() => {
    fetchTickets();
  }, [filters]);

  return (
    <div className="support-dashboard">
      <div className="dashboard-header">
        <h1>Support Dashboard</h1>
        <button 
          className="refresh-button"
          onClick={fetchTickets}
          disabled={loading}
        >
          {loading ? '🔄' : '↻'} Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="severity-filter">Severity:</label>
          <select
            id="severity-filter"
            value={filters.severity}
            onChange={(e) => handleFilterChange('severity', e.target.value)}
          >
            <option value="all">All Severities</option>
            <option value="P1 - Critical">P1 - Critical</option>
            <option value="P2 - High">P2 - High</option>
            <option value="P3 - Normal">P3 - Normal</option>
            <option value="P4 - Low">P4 - Low</option>
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading tickets...</p>
        </div>
      )}

      {/* Tickets Table */}
      {!loading && (
        <div className="tickets-table-container">
          <table className="tickets-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('ticket_id')} className="sortable">
                  Ticket ID {filters.sortBy === 'ticket_id' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th onClick={() => handleSort('contact_name')} className="sortable">
                  Contact {filters.sortBy === 'contact_name' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th onClick={() => handleSort('issue_type')} className="sortable">
                  Issue Type {filters.sortBy === 'issue_type' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th onClick={() => handleSort('severity')} className="sortable">
                  Severity {filters.sortBy === 'severity' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th>Summary</th>
                <th onClick={() => handleSort('status')} className="sortable">
                  Status {filters.sortBy === 'status' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th onClick={() => handleSort('created_at')} className="sortable">
                  Created {filters.sortBy === 'created_at' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-tickets">
                    No tickets found
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr key={ticket.ticket_id}>
                    <td className="ticket-id">{ticket.ticket_id}</td>
                    <td>
                      <div className="contact-info">
                        <div className="contact-name">{ticket.contact_name}</div>
                        <div className="contact-email">{ticket.contact_email}</div>
                      </div>
                    </td>
                    <td>{ticket.issue_type}</td>
                    <td>
                      <span className={getSeverityBadgeClass(ticket.severity)}>
                        {ticket.severity}
                      </span>
                    </td>
                    <td className="summary-cell" title={ticket.summary}>
                      {ticket.summary}
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(ticket.status)}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{formatDate(ticket.created_at)}</td>
                    <td>
                      <div className="actions-cell">
                        <select
                          value={ticket.status}
                          onChange={(e) => updateTicketStatus(ticket.ticket_id, e.target.value)}
                          className="status-select"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && tickets.length > 0 && (
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Tickets:</span>
            <span className="stat-value">{tickets.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Open:</span>
            <span className="stat-value">{tickets.filter(t => t.status === 'open').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">In Progress:</span>
            <span className="stat-value">{tickets.filter(t => t.status === 'in_progress').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Resolved:</span>
            <span className="stat-value">{tickets.filter(t => t.status === 'resolved').length}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportDashboard;
